import React, { useEffect, useState } from "react";
import TableCompo from "../Table/TableCompo";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, CardContent ,Button, useMediaQuery, useTheme, Stack} from "@mui/material";
import { Tooltip } from "@mui/material";
import { fetchGiftsRequest } from "../../../redux/Gifts/GiftsSlice";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import useCsvConverter from "../../../hooks/convertToCSV";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";


const NewGifts = () => {
  const dispatch = useDispatch();
  const { convertToCsv } = useCsvConverter();
  const Gifts = useSelector((state) => state.gifts || {});
  const { data = [], total = 0 } = Gifts;

  const package_name = useSelector(
    (state) => state?.appConfig?.configData?.package_name || ""
  );
  const decimal = useSelector(
    (state) => state?.appConfig?.configData?.decimalPrecision || 4,
  );

  const [pagination, setPagination] = useState({ page: 0, limit: 5 });
  const [search, setSearch] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exporting, setExporting] = useState(false);

  const formatDateTimeRange = (date, type) => {
  if (!date) return null;
  const d = new Date(date);
  if (type === "start") {
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  if (type === "end") {
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
};

  useEffect(() => {
    const payload = { start: pagination.page * pagination.limit, limit: pagination.limit, search };
    if (startDate && endDate) {
      payload.startDate = formatDateTimeRange(startDate, "start");
      payload.endDate = formatDateTimeRange(endDate, "end");
    }
    dispatch(
      fetchGiftsRequest(payload)
    );
  }, [dispatch, pagination, search]);

  const handleGetDetails = () => {
  if (!startDate || !endDate) {
    toast.error("Please select both start and end dates.");
    return;
  }
  if (startDate > endDate) {
    toast.error("Start Date cannot be after End Date");
    return;
  }

  setPagination((prev) => ({ ...prev, page: 0 }));
  dispatch(
    fetchGiftsRequest({
      start: 0,
      limit: pagination.limit,
      search,
      startDate: formatDateTimeRange(startDate, "start"),
      endDate: formatDateTimeRange(endDate, "end"),
    })
  );
};

  const handleTableChange = (action, tableState) => {
    const dateParams =
    startDate && endDate
      ? {
          startDate: formatDateTimeRange(startDate, "start"),
          endDate: formatDateTimeRange(endDate, "end"),
        }
      : {};
    switch (action) {
      case "changePage":
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        dispatch(
        fetchGiftsRequest({
          start: tableState.page * pagination.limit,
          limit: pagination.limit,
          search,
          ...dateParams,
        })
      );
        break;

      case "changeRowsPerPage":
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        dispatch(
          fetchGiftsRequest({
            start: 0,
            limit: tableState.rowsPerPage,
            search,
            ...dateParams,
          })
        );
        break;

      case "search":
        const searched = tableState.searchText || "";
        setSearch(tableState.searchText || "");
        setPagination((prev) => ({ ...prev, page: 0 }));
        dispatch(
          fetchGiftsRequest({
            start: 0,
            limit: pagination.limit,
            search: searched,
            ...dateParams,
          })
        );
        break;

      default:
        break;
    }
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Created At",
      render: (val, row) => {
        if (!row?.createdAt) return "N.A";

        const date = new Date(row.createdAt);
        const formattedDate = date.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        return (
          <Tooltip title={formattedDate} arrow>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "customer",
      headerName: "Name",
      render: (val, row) =>
        row.customer
          ? `${row.customer.firstName || ""} ${row.customer.middleName || ""} ${row.customer.lastName || ""}`
          : row.giftFrom || "N.A",
    },
    {
      field: "metalName",
      headerName: "Metal",
    },
    {
      field: "weight",
      headerName: "Weight",
      render: (val, row) => {
        return row?.weight != null
          ? Number(row.weight).toFixed(decimal)
          : "N.A";
      },
    },
    {
      field: "giftTo",
      headerName: "Gift To",
    },
    {
      field: "giftFrom",
      headerName: "Gift From",
      render: (val, row) => row.giftFrom || "N.A",
    },
    {
      field: "occasion",
      headerName: "Occasion",
    },
    {
      field: "isAccepted",
      headerName: "Accepted",
      render: (val, row) => (row.isAccepted ? "Yes" : "No"),
    },
  ];

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    console.log("Store CSV API called");
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }
    if (startDate > endDate) {
      toast.error("Start Date cannot be after End Date");
      return;
    }
    setExporting(true);

    const query = {};
    if (startDate && endDate) {
      query.startDate = formatDateTimeRange(startDate, "start");
      query.endDate = formatDateTimeRange(endDate, "end");
    }
    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/gift/giftsCsv",
        query,
        callback: (response) => {
          setExporting(false);
          if (response?.success) {
            toast.success(response.message || "Export started.");
          } else {
            toast.error(response?.message || "Export failed.");
          }
        },
      })
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          New Gifts
        </Typography>

        <CardContent sx={{ height: "100%", width: "100%" }}>
          <Box display="flex" gap={1.5} px={2} pb={1} flexWrap="wrap" alignItems="center">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    gap={1.5}
                    px={2}
                    py={1}
                  >
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      maxDate={endDate || new Date()}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            minWidth: 160,
                            "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                            "& .MuiInputLabel-root": { fontSize: 13 },
                          },
                        },
                      }}
                    />
                                      
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      minDate={startDate}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            minWidth: 160,
                            "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                            "& .MuiInputLabel-root": { fontSize: 13 },
                          },
                        },
                      }}
                    />
                    <Button
                      variant="outlined"
                      fullWidth={isMobile}
                      sx={{
                        height: 36,
                        minWidth: 120,
                        fontSize: 13,
                        textTransform: "none",
                        px: 2,
                        whiteSpace: "nowrap",
                      }}
                        onClick={handleGetDetails}
                    >
                        Get Details
                    </Button>
                  </Box>
                </LocalizationProvider>
          </Box>
          <TableCompo
            columns={columns}
            data={JSON.parse(JSON.stringify(data))}
            totalCount={total}
            onTableChange={handleTableChange}
            page={pagination.page}
            limit={pagination.limit}
            extraButtons={
              <Stack direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDownloadStoreCsv}
                  disabled={exporting}
                >
                  {exporting ? "Exporting..." : "Export Report"}
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  component="a"
                  href={`${process.env.REACT_APP_API_BASE_URL}/static_data/${package_name}/gift.xlsx?nocache=${Date.now()}`}
                  download="gift.xlsx"
                >
                  Download
                </Button>
              </Stack>
            }
          />
        </CardContent>
      </Box>
    </Box>
  );
};

export default NewGifts;
